import {CurrencyService} from '../../services/CurrencyService';
import Experiments from '@wix/wix-experiments';
import {CurrencySymbols, CurrencySymbolsMap} from '../../settings/services/currency.utility';
import {getTranslations} from '@wix/wixstores-client-core/dist/src/viewer-script/utils';
import {IHostProps} from '@wix/native-components-infra/dist/src/types/types';
import {CurrencyConverterSiteStore} from './currencyConverterSiteStore';
import {SiteCurrency} from '@wix/ambassador-currency-site-settings/types';

export interface IPropsInjectedByViewerScript {
  currencySymbols: CurrencySymbols;
  userCurrencies: string[];
  selectedCurrency: string;
  onCurrencySelected(code: string): void;
  translations: any;
  shouldUseOverflowHack: boolean;
  isRtl: boolean;
  isMobile: boolean;
  host: IHostProps;
}

const CURRENCY_CONVERTER_EXPERIMENT_SCOPE = 'currency';

export class CurrencyConverterStore {
  private readonly updateComponent: (props: Partial<IPropsInjectedByViewerScript>) => void;
  private readonly currencyService: CurrencyService;
  private currencySymbols: CurrencySymbols;
  private userCurrencies: string[];
  private selectedCurrency: string;
  private readonly siteStore: CurrencyConverterSiteStore;
  private experiments: Experiments;

  constructor({
    siteStore,
    currencyService,
    updateComponent,
  }: {
    siteStore: CurrencyConverterSiteStore;
    currencyService: CurrencyService;
    updateComponent(props: Partial<IPropsInjectedByViewerScript>): void;
  }) {
    this.updateComponent = updateComponent;
    this.currencyService = currencyService;
    this.siteStore = siteStore;

    this.siteStore.location.onChange(() => {
      if (this.selectedCurrency !== this.siteStore.location.query.currency) {
        this.updateComponent({
          selectedCurrency: this.siteStore.location.query.currency,
        });
      }
    });
  }

  private onCurrencySelected = (currency: string) => {
    if (currency === this.siteStore.siteMainCurrency) {
      this.siteStore.removeQueryParams(['currency']);
    } else {
      this.siteStore.updateQueryParams({currency});
    }
    this.siteStore.biLogger.currencyConverterChooseSiteCurrencyDisplay({
      newValue: currency,
      siteCurrency: this.siteStore.siteMainCurrency,
    });
  };

  private getUserCurrenciesWithMainSiteCurrencyFirst(mainCurrency: string, additionalCurrencies: SiteCurrency[]) {
    return [mainCurrency, ...additionalCurrencies.map(({code}) => code).filter(code => code !== mainCurrency)];
  }

  public async setInitialState(): Promise<any> {
    this.experiments = new Experiments();
    const [translations] = await Promise.all([
      getTranslations(
        `${this.siteStore.baseUrls.currencyConverterBaseUrl}assets/locales/widget/messages_${this.siteStore.locale}.json`
      ),
      // TEMPORARY FOR THE OVERFLOW HACK
      this.siteStore.isEditorMode() ? Promise.resolve() : this.experiments.load(CURRENCY_CONVERTER_EXPERIMENT_SCOPE),
    ]);

    this.selectedCurrency = this.siteStore.currentCurrencyCode;
    this.userCurrencies = this.getUserCurrenciesWithMainSiteCurrencyFirst(this.siteStore.siteMainCurrency, [
      {code: this.selectedCurrency},
    ]);
    this.loadInitialData();
    const shouldUseOverflowHack = this.experiments.enabled('specs.currency.ContainerParentOverflowHack');

    this.updateComponent({
      userCurrencies: this.userCurrencies,
      currencySymbols: this.currencySymbols,
      selectedCurrency: this.selectedCurrency,
      onCurrencySelected: this.onCurrencySelected,
      translations,
      shouldUseOverflowHack,
      isRtl: this.siteStore.isRTL(),
      isMobile: this.siteStore.isMobile(),
    });
  }

  public async loadInitialData() {
    const [allCurrencies, userCurrencies] = await Promise.all([
      this.currencyService.getAvailableCurrenciesList(),
      this.currencyService.getCurrencies(),
    ]);

    this.currencySymbols = CurrencySymbolsMap(allCurrencies);

    this.selectedCurrency = this.siteStore.currentCurrencyCode;
    this.userCurrencies = this.getUserCurrenciesWithMainSiteCurrencyFirst(
      this.siteStore.siteMainCurrency,
      userCurrencies
    );
    const selectedCurrency = this.userCurrencies.find(code => code === this.selectedCurrency);
    if (!selectedCurrency) {
      this.userCurrencies.push(this.selectedCurrency);
    }

    this.updateComponent({
      userCurrencies: this.userCurrencies,
      currencySymbols: this.currencySymbols,
    });
  }
}
